import { useContext, useEffect, useId, useRef } from "react";
import type {
  EventPath,
  RelayPayload,
  RelaySchema,
  RelayStatus,
} from "../protocol/event-types";
import { RelayContext } from "./relay-provider";

export function createRelayHooks<T extends RelaySchema>() {
  function useRelay<const E extends EventPath<T>>(opts: {
    channels?: readonly (string | null | undefined)[];
    events?: readonly E[];
    onData?: (payload: RelayPayload<T, E>) => void;
    enabled?: boolean;
    /**
     * Keep the tab's stream open while the page is hidden, for as long as this subscription is
     * registered. For a subscription waiting on the outcome of work the page started (an upload
     * being processed) whose handler must run even in a background tab. Without it, a page hidden
     * 5 minutes closes the stream, and showing it again replays what it missed.
     */
    liveWhileHidden?: boolean;
  }): { status: RelayStatus } {
    const context = useContext(RelayContext);
    if (!context)
      throw new Error("useRelay must be used inside <RelayProvider>");
    const id = useId();
    const onDataRef = useRef(opts.onData);
    onDataRef.current = opts.onData;
    const { register, unregister } = context;

    const channelsKey = JSON.stringify(
      (opts.channels ?? []).filter((c): c is string => Boolean(c)),
    );
    const eventsKey = JSON.stringify(opts.events ?? null);

    useEffect(() => {
      const channels: string[] = JSON.parse(channelsKey);
      const events: string[] | null = JSON.parse(eventsKey);
      if (opts.enabled === false || channels.length === 0)
        return unregister(id);
      register(id, {
        channels: new Set(channels),
        liveWhileHidden: opts.liveWhileHidden === true,
        onEvent: (event) => {
          if (events && !events.includes(event.event)) return;
          // Frame came from our own relay and the event path was schema-parsed at publish time.
          onDataRef.current?.({
            event: event.event,
            data: event.data,
            channel: event.channel,
            origin: event.origin ?? null,
            writeId: event.writeId ?? null,
          } as RelayPayload<T, E>);
        },
      });
      return () => unregister(id);
    }, [
      id,
      channelsKey,
      eventsKey,
      opts.enabled,
      opts.liveWhileHidden,
      register,
      unregister,
    ]);

    return { status: context.status };
  }
  return { useRelay };
}
